import React, { Dispatch, FC, SetStateAction, useEffect, useState, useMemo, useCallback, useRef } from 'react';

import Spotlights from 'components/Spotlights';
import LoaderContent from 'components/UI/LoaderContent';
import useTheme from 'helpers/useTheme';
import getStyles from 'helpers/getStyles';

import { CardTypeOptions, ECollectionLayoutOptions, UserObjectType } from 'constants/graphqlTypes';
import { useGetSpotlights } from 'graphQL/spotlights/hook';
import { COLORS } from 'styles/constants';
import { useNavigationRef } from 'components/Navigation/helpers/hooks';
import { useProfileContext } from 'helpers/useProfileProvider';

import ListGrid from 'components/List/Grid';
import PageBackground from 'components/Profile/PageBackground';
import usePageType from 'helpers/usePageType';
import { useGetUserCards, useGetFilteredUserPicks, useGetUserPickedTopics } from 'graphQL/cards/userCards/hooks';
import { useGetUserSparks, useGetSparkCategories } from 'graphQL/sparks/hooks';
import ProfileVideoBackground from './ProfileVideoBackground';
import Info from './Info';
import { StyledSection, StyledSlidingWrapper, StyledSlidingContainer, StyledSlidingPanel } from './styled';
import ProfileContent from '../ProfileContent';
import PicksFilter from '../PicksFilter/PicksFilter';
import SparksList from '../SparksList';

interface IProfileTopProps {
  userData?: UserObjectType;
  loading: boolean;
  setHideSpotlights: Dispatch<SetStateAction<boolean>>;
  hideSpotlights: boolean;
  onGradientChange?: (gradientStart: string | null, gradientEnd: string | null) => void;
}

const ProfileTop: FC<IProfileTopProps> = ({
  userData,
  loading,
  setHideSpotlights,
  hideSpotlights,
  onGradientChange,
}) => {
  const [index, setIndex] = useState(0);
  const [hiddenCards, setHiddenCards] = useState<string[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isSparksTabReady, setIsSparksTabReady] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{
    topicIds?: number[];
    mediaTagNames?: string[];
  }>({});

  const spotlightsData = useGetSpotlights();
  const isDark = useTheme();
  const { isMyProfile } = usePageType();
  const { cards, loading: spotlightsLoading, listsCount } = spotlightsData;
  const { gradientStart, gradientEnd } = getStyles(cards[index]?.cardStyle);
  const [hideGradient, setHideGradient] = useState(false);
  const {
    setGradientStart,
    setGradientEnd,
    setActiveTabIndex: setContextActiveTabIndex,
    setHasSparkCategories,
  } = useProfileContext();
  const { userInfo, userStyle, id: userId, username } = userData ?? {};
  const type = userStyle?.layoutCollection === ECollectionLayoutOptions.ImageGrid ? CardTypeOptions.Card : undefined;
  const { recentCards, loadMore, totalCount, loading: loadingItems } = useGetUserCards({
    type,
    includeQuestionResponse: true,
    username,
  });

  // Fetch user sparks
  const { items: sparks, totalCount: sparksCount, loading: loadingSparks } = useGetUserSparks(
    userId ? { userId } : undefined
  );

  // Fetch spark categories to determine if Sparks tab should be shown
  const { totalCount: sparkCategoriesCount } = useGetSparkCategories();
  const hasSparkCategories = sparkCategoriesCount > 0;

  // Get media tags to convert names to IDs
  const { mediaTags } = useGetUserPickedTopics();

  // Convert media tag names to IDs for the GraphQL query
  const mediaTagIds = useMemo(() => {
    if (!selectedFilters.mediaTagNames?.length || !mediaTags?.length) return undefined;

    // Convert string IDs to numbers if needed
    const mediaTagNameToId = new Map(
      mediaTags.map((tag: { id: string | number; name: string }) => [
        tag.name,
        typeof tag.id === 'string' ? parseInt(tag.id, 10) : tag.id,
      ])
    );
    const ids = selectedFilters.mediaTagNames
      .map((name) => mediaTagNameToId.get(name))
      .filter((id): id is number => id !== undefined && !Number.isNaN(id));

    return ids;
  }, [selectedFilters.mediaTagNames, mediaTags]);

  // Use filtered picks when filters are applied, otherwise use regular picks
  const {
    filteredPicks,
    loadMore: loadMoreFiltered,
    totalCount: filteredTotalCount,
    loading: loadingFilteredItems,
  } = useGetFilteredUserPicks({
    topicIds: selectedFilters.topicIds,
    mediaTagIds,
  });

  const hasActiveFilters = selectedFilters.topicIds?.length || selectedFilters.mediaTagNames?.length;

  // Use the appropriate data source based on whether filters are applied
  const displayCards = hasActiveFilters ? filteredPicks : recentCards;
  const displayLoadMore = hasActiveFilters ? loadMoreFiltered : loadMore;
  const displayTotalCount = hasActiveFilters ? filteredTotalCount : totalCount;
  const displayLoading = hasActiveFilters ? loadingFilteredItems : loadingItems;
  const { avatarColorGradientStart, avatarColorGradientEnd } = userInfo ?? {};

  const isCurrentSpotlightHidden = hiddenCards.includes(cards[index]?.id);

  const allReady = totalCount !== 0 || sparksCount !== 0 || listsCount !== 0;

  useEffect(() => {
    if (gradientEnd && gradientStart) {
      setGradientEnd?.(gradientEnd);
      setGradientStart?.(gradientStart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradientStart, gradientEnd]);

  // Call the parent callback when gradient changes
  useEffect(() => {
    const currentGradientStart = activeTabIndex === 0 ? gradientEnd ?? avatarColorGradientEnd ?? null : null;
    const currentGradientEnd = activeTabIndex === 0 ? gradientEnd ?? avatarColorGradientEnd ?? null : null;
    onGradientChange?.(currentGradientStart, currentGradientEnd);
  }, [gradientStart, gradientEnd, avatarColorGradientStart, avatarColorGradientEnd, activeTabIndex, onGradientChange]);

  const fontColor = isDark || !hideGradient ? COLORS.white[100] : COLORS.brown.dark[100];
  // Spotlights and PicksFilter font color should contrast with the list area background
  const listAreaBaseColor = gradientEnd ?? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100]);
  const spotlightsFontColor = listAreaBaseColor === COLORS.white[100] ? COLORS.brown.dark[100] : COLORS.white[100];
  const cardIds = cards.map(({ id }) => id);
  const spotlightsChangeKey = JSON.stringify(cardIds);
  const ref = useNavigationRef(fontColor);

  useEffect(() => {
    setIndex(0);
  }, [spotlightsChangeKey]);

  const hiddenCardIds = useMemo(() => cards.filter(({ isHidden }) => !!isHidden).map(({ id }) => id), [cards]);

  useEffect(() => {
    setHiddenCards(hiddenCardIds);
  }, [hiddenCardIds]);

  useEffect(() => {
    setHideSpotlights(spotlightsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlightsLoading]);

  useEffect(() => {
    setHideGradient(!cards.length || isCurrentSpotlightHidden || spotlightsLoading || loading);
  }, [isCurrentSpotlightHidden, cards.length, spotlightsLoading, loading]);

  useEffect(() => {
    setActiveTabIndex(0);
    setIsSparksTabReady(false);
  }, [userId]);

  // Track when Sparks tab slide transition completes (350ms transition duration)
  useEffect(() => {
    if (activeTabIndex === 1) {
      // Sparks tab is now active, wait for slide transition to complete
      const timer = setTimeout(() => {
        setIsSparksTabReady(true);
      }, 350);
      return () => clearTimeout(timer);
    }
    // Not on Sparks tab, reset ready state
    setIsSparksTabReady(false);
    return undefined;
  }, [activeTabIndex]);

  // Sync activeTabIndex with context whenever it changes
  useEffect(() => {
    setContextActiveTabIndex?.(activeTabIndex);
  }, [activeTabIndex, setContextActiveTabIndex]);

  // Sync hasSparkCategories with context
  useEffect(() => {
    setHasSparkCategories?.(hasSparkCategories);
  }, [hasSparkCategories, setHasSparkCategories]);

  const handleTabChange = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
  };

  // Swipe handling for tab switching
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      // Only trigger swipe if horizontal movement is greater than vertical (to avoid interfering with scroll)
      // and if the swipe distance is significant enough (minimum 50px)
      const minSwipeDistance = 50;
      const maxTabIndex = hasSparkCategories ? 2 : 1; // 0 = Picks, 1 = Sparks (if available), 2 = Lists OR 0 = Picks, 1 = Lists
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX < 0 && activeTabIndex < maxTabIndex) {
          // Swipe left -> go to next tab
          setActiveTabIndex(activeTabIndex + 1);
        } else if (deltaX > 0 && activeTabIndex > 0) {
          // Swipe right -> go to previous tab
          setActiveTabIndex(activeTabIndex - 1);
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
    },
    [activeTabIndex]
  );

  // No overlay; Spotlights ref no-op
  const spotlightsRef = useCallback(() => {}, []);

  const handleFiltersChange = (filters: { topicIds?: number[]; mediaTagNames?: string[] }) => {
    setSelectedFilters(filters);
  };

  const renderContent = () => {
    if (loading) {
      return <LoaderContent isCentered isDark={isDark} isFullScreen />;
    }

    // For Lists tab or Sparks tab with no sparks, use user's avatar gradient
    // Lists tab index depends on whether spark categories exist: index 2 if sparks exist, index 1 if not
    const listsTabIndex = hasSparkCategories ? 2 : 1;
    const sparksTabIndex = hasSparkCategories ? 1 : -1; // -1 if no spark categories (tab doesn't exist)
    const useUserGradient =
      activeTabIndex === listsTabIndex || (activeTabIndex === sparksTabIndex && sparksCount === 0);
    const pageBase = useUserGradient
      ? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
      : gradientEnd ?? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100]);
    const overlayStart = useUserGradient
      ? avatarColorGradientEnd ?? null
      : gradientEnd ?? avatarColorGradientEnd ?? null;
    // Safe default height; ListGrid overlay can be removed; this overlay spans a reasonable area
    const defaultOverlayHeight = 420;

    return (
      <PageBackground
        startColor={overlayStart}
        baseColor={pageBase}
        overlayHeightPx={defaultOverlayHeight}
        foregroundLayer={<ProfileVideoBackground heightPx={defaultOverlayHeight} />}
      >
        <Info
          isDark={isDark}
          showGradient={useUserGradient}
          userData={userData}
          fontColor={fontColor}
          noContent={!cards.length}
          hideGradient={hideGradient}
          onTabChange={handleTabChange}
          activeTabIndex={activeTabIndex}
          cardsCount={allReady ? displayTotalCount : 0}
          sparksCount={allReady ? sparksCount : 0}
          listsCount={allReady ? listsCount : 0}
          gradientStart={useUserGradient ? avatarColorGradientEnd : null}
          gradientEnd={useUserGradient ? avatarColorGradientEnd : null}
          showStyledGrain={false}
          hasSparkCategories={hasSparkCategories}
        />

        {activeTabIndex === 0 && !hideSpotlights && (
          <Spotlights
            hiddenCards={hiddenCards}
            setHiddenCards={setHiddenCards}
            spotlightsData={spotlightsData}
            fontColor={spotlightsFontColor}
            setIndex={setIndex}
            containerRef={spotlightsRef}
            key={spotlightsChangeKey}
          />
        )}

        <StyledSlidingWrapper onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <StyledSlidingContainer activeIndex={activeTabIndex} panelCount={hasSparkCategories ? 3 : 2}>
            <StyledSlidingPanel panelCount={hasSparkCategories ? 3 : 2} isDark={isDark}>
              <PicksFilter onFiltersChange={handleFiltersChange} backgroundColor={listAreaBaseColor} />
              {userData && (
                <ListGrid
                  hideActionsInfo={isMyProfile}
                  layout={ECollectionLayoutOptions.Default}
                  items={displayCards}
                  loadMore={displayLoadMore}
                  loading={displayLoading}
                  isDarkFromProps={isDark}
                  isProfilePicks
                  isMyProfile={isMyProfile}
                  gradientStartColor={
                    gradientEnd ?? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
                  }
                />
              )}
            </StyledSlidingPanel>

            {hasSparkCategories && (
              <StyledSlidingPanel panelCount={3} isDark={isDark}>
                {userData && (
                  <SparksList
                    sparks={sparks}
                    loading={loadingSparks}
                    isMyProfile={isMyProfile}
                    fontColor={spotlightsFontColor}
                    isDark={isDark}
                    gradientStartColor={
                      sparksCount > 0
                        ? gradientEnd ?? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
                        : avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
                    }
                    isTabReady={isSparksTabReady}
                  />
                )}
              </StyledSlidingPanel>
            )}

            <StyledSlidingPanel panelCount={hasSparkCategories ? 3 : 2} isDark={isDark}>
              {userData && <ProfileContent gradientStartColor={avatarColorGradientEnd ?? undefined} />}
            </StyledSlidingPanel>
          </StyledSlidingContainer>
        </StyledSlidingWrapper>
      </PageBackground>
    );
  };

  // For Lists tab or Sparks tab with no sparks, use user's avatar gradient
  // Lists tab index depends on whether spark categories exist: index 2 if sparks exist, index 1 if not
  const listsTabIndexForSection = hasSparkCategories ? 2 : 1;
  const sparksTabIndexForSection = hasSparkCategories ? 1 : -1;
  const useUserGradientForSection =
    activeTabIndex === listsTabIndexForSection || (activeTabIndex === sparksTabIndexForSection && sparksCount === 0);

  return (
    <StyledSection
      ref={cards.length ? ref : null}
      fontColor={fontColor}
      fixedHeight={!!cards.length}
      isDark={isDark}
      bgColor={
        useUserGradientForSection
          ? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
          : gradientEnd ?? avatarColorGradientEnd ?? (isDark ? COLORS.black[100] : COLORS.white[100])
      }
    >
      {renderContent()}
    </StyledSection>
  );
};

export default ProfileTop;
